import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    Tab,
    Tabs,
    Typography,
    makeStyles,
} from '@material-ui/core';
import MapOutlined from '@material-ui/icons/MapOutlined';
import ReportProblemOutlined from '@material-ui/icons/ReportProblemOutlined';
import { useTranslate } from 'react-admin';
import { PRESTATAIRE_TYPES, PrestataireTypeValue } from './constants';
import DepartmentMapPicker, { MapMarker } from './DepartmentMapPicker';
import DepartmentSelectionPanel from './DepartmentSelectionPanel';
import { addDepartmentCode } from './departments';
import { searchIgnAddresses } from './ignGeocode';
import { PrestataireScope } from './types';

const useStyles = makeStyles({
    typeCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginTop: 8,
    },
    typeCard: {
        margin: 0,
        padding: '8px 12px 8px 4px',
        border: '1px solid #d0d7de',
        borderRadius: 8,
        transition: 'border-color 120ms, background 120ms',
        '&:hover': { borderColor: '#1d6fb8' },
    },
    typeCardChecked: {
        borderColor: '#1d6fb8',
        background: '#eef4fb',
    },
    typeCardError: {
        borderColor: '#d32f2f',
    },
    emptyZone: {
        marginTop: 16,
        padding: 24,
        border: '1px dashed #c3ccd5',
        borderRadius: 8,
        textAlign: 'center',
        color: '#5b7388',
    },
    tab: {
        minWidth: 0,
        textTransform: 'none',
        fontWeight: 600,
    },
});

interface TypesAndDepartmentsFieldsProps {
    scopes: PrestataireScope[];
    onScopesChange: (scopes: PrestataireScope[]) => void;
    initialAddress?: string;
    disabled?: boolean;
    errorTypes?: boolean;
    errorDepartments?: boolean;
}

const TypesAndDepartmentsFields: React.FC<TypesAndDepartmentsFieldsProps> = ({
    scopes,
    onScopesChange,
    initialAddress,
    disabled,
    errorTypes,
    errorDepartments,
}) => {
    const classes = useStyles();
    const translate = useTranslate();
    const selectedTypes = scopes.map((scope) => scope.type);
    const [activeType, setActiveType] = useState<string | null>(selectedTypes[0] || null);
    const [addressDepartment, setAddressDepartment] = useState<number | null>(null);
    const [companyMarker, setCompanyMarker] = useState<MapMarker | null>(null);
    const lastGeocodedAddress = useRef('');
    // The geocoding promise resolves after renders: read the latest scopes, not the ones it closed over.
    const scopesRef = useRef(scopes);
    scopesRef.current = scopes;

    const typeLabel = (type: string) => translate(`app.prestataires.types.${type}`, { _: type });

    useEffect(() => {
        if (!activeType || !selectedTypes.includes(activeType)) {
            setActiveType(selectedTypes[0] || null);
        }
    }, [activeType, selectedTypes]);

    // The company's own department is pre-selected for every type, once per address.
    useEffect(() => {
        const address = (initialAddress || '').trim();
        if (!address) {
            lastGeocodedAddress.current = '';
            return undefined;
        }
        if (address === lastGeocodedAddress.current) {
            return undefined;
        }
        let cancelled = false;
        lastGeocodedAddress.current = address;
        searchIgnAddresses(address)
            .then((results) => {
                const feature = results[0];
                if (cancelled || !feature) {
                    return;
                }
                setCompanyMarker({ longitude: feature.longitude, latitude: feature.latitude });
                setAddressDepartment(feature.departmentCode);
                const code = feature.departmentCode;
                const current = scopesRef.current;
                if (code && current.some((scope) => !(scope.departmentCodes || []).includes(code))) {
                    onScopesChange(
                        current.map((scope) => ({
                            ...scope,
                            departmentCodes: addDepartmentCode(scope.departmentCodes || [], code),
                        })),
                    );
                }
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [initialAddress, onScopesChange]);

    const toggleType = (type: PrestataireTypeValue) => {
        if (selectedTypes.includes(type)) {
            onScopesChange(scopes.filter((scope) => scope.type !== type));
            return;
        }
        onScopesChange([...scopes, { type, departmentCodes: addressDepartment ? [addressDepartment] : [] }]);
        setActiveType(type);
    };

    const updateDepartments = (type: string, departmentCodes: number[]) => {
        onScopesChange(scopes.map((scope) => (scope.type === type ? { ...scope, departmentCodes } : scope)));
    };

    const applyToAllTypes = (departmentCodes: number[]) => {
        onScopesChange(scopes.map((scope) => ({ ...scope, departmentCodes: [...departmentCodes] })));
    };

    const activeScope = scopes.find((scope) => scope.type === activeType) || null;
    const otherScopes = scopes.filter((scope) => scope.type !== activeType);
    const otherDepartmentCodes = useMemo(
        () => Array.from(new Set(otherScopes.flatMap((scope) => scope.departmentCodes || []))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(otherScopes)],
    );
    const missingTypes = scopes.filter((scope) => (scope.departmentCodes || []).length === 0);

    return (
        <>
            <FormControl component='fieldset' margin='normal' error={errorTypes} disabled={disabled} fullWidth>
                <FormLabel component='legend'>{translate('app.prestataires.fields.types')} *</FormLabel>
                <Box className={classes.typeCards}>
                    {PRESTATAIRE_TYPES.map((type) => {
                        const checked = selectedTypes.includes(type);
                        return (
                            <FormControlLabel
                                key={type}
                                className={[
                                    classes.typeCard,
                                    checked ? classes.typeCardChecked : '',
                                    errorTypes ? classes.typeCardError : '',
                                ].join(' ')}
                                control={<Checkbox color='primary' checked={checked} onChange={() => toggleType(type)} />}
                                label={translate(`app.prestataires.types.${type}`)}
                            />
                        );
                    })}
                </Box>
                {errorTypes && (
                    <FormHelperText>{translate('app.prestataires.validation.typesRequired')}</FormHelperText>
                )}
            </FormControl>

            {!activeScope && (
                <Box className={classes.emptyZone}>
                    <MapOutlined />
                    <Typography variant='body2'>{translate('app.prestataires.zone.pickTypeFirst')}</Typography>
                </Box>
            )}

            {activeScope && (
                <Box marginTop={1}>
                    <FormLabel component='legend'>{translate('app.prestataires.zone.title')}</FormLabel>
                    <Typography variant='caption' color='textSecondary' component='p'>
                        {translate('app.prestataires.fields.departmentsMapHelp')}
                    </Typography>
                    <Tabs
                        value={activeScope.type}
                        onChange={(_event, value) => setActiveType(value)}
                        indicatorColor='primary'
                        textColor='primary'
                        variant='scrollable'
                    >
                        {scopes.map((scope) => {
                            const count = (scope.departmentCodes || []).length;
                            const missing = errorDepartments && count === 0;
                            return (
                                <Tab
                                    key={scope.type}
                                    value={scope.type}
                                    className={classes.tab}
                                    label={`${typeLabel(scope.type)} (${count})`}
                                    icon={missing ? <ReportProblemOutlined fontSize='small' /> : undefined}
                                    style={missing ? { color: '#d32f2f', minHeight: 48, flexDirection: 'row' } : undefined}
                                />
                            );
                        })}
                    </Tabs>

                    <Grid container spacing={2} style={{ marginTop: 4 }}>
                        <Grid item xs={12} md={8}>
                            <DepartmentMapPicker
                                departmentCodes={activeScope.departmentCodes || []}
                                otherDepartmentCodes={otherDepartmentCodes}
                                onChange={(codes) => updateDepartments(activeScope.type, codes)}
                                marker={companyMarker}
                                initialAddress={initialAddress}
                                activeLabel={typeLabel(activeScope.type)}
                                disabled={disabled}
                                error={errorDepartments && (activeScope.departmentCodes || []).length === 0}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <DepartmentSelectionPanel
                                departmentCodes={activeScope.departmentCodes || []}
                                onChange={(codes) => updateDepartments(activeScope.type, codes)}
                                otherScopes={otherScopes}
                                onApplyToAll={() => applyToAllTypes(activeScope.departmentCodes || [])}
                                typeLabel={typeLabel}
                                disabled={disabled}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}
            {errorDepartments &&
                missingTypes.map((scope) => (
                    <FormHelperText key={scope.type} error>
                        {translate('app.prestataires.validation.departmentsRequiredForType', {
                            type: typeLabel(scope.type),
                        })}
                    </FormHelperText>
                ))}
        </>
    );
};

export default TypesAndDepartmentsFields;
